import WorkspaceShell from "./WorkspaceShell";
import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import api from "../api";

const APPLICANT_ALLOWED_PATHS = [
  "/tenant/home",
  "/tenant/apartments",
  "/tenant/applications",
];

const hasPathAccessForApplicant = (pathname) => {
  if (APPLICANT_ALLOWED_PATHS.includes(pathname)) {
    return true;
  }
  if (pathname.startsWith("/tenant/apartments/")) {
    return true;
  }
  if (pathname.startsWith("/tenant/apply/")) {
    return true;
  }
  return false;
};

const truthy = (value) => value === true || value === 1 || value === "1";

const hasLinkedUnitFromProfile = (profile) => {
  if (!profile || typeof profile !== "object") {
    return false;
  }

  const idFields = [
    "property_id",
    "rental_id",
    "unit_id",
    "assigned_property_id",
    "assigned_unit_id",
  ];
  const hasIdLink = idFields.some((field) => profile[field] !== null && profile[field] !== undefined);
  if (hasIdLink) {
    return true;
  }

  const boolFields = [
    "has_active_rental",
    "hasLinkedUnit",
    "is_linked",
    "is_tenant_active",
    "approved_tenant",
  ];
  if (boolFields.some((field) => truthy(profile[field]))) {
    return true;
  }

  return false;
};

export default function TenantLayout({ children, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState(
    localStorage.getItem("username") || "Tenant"
  );
  const [loadingUser, setLoadingUser] = useState(true);
  const [hasLinkedUnit, setHasLinkedUnit] = useState(
    () => localStorage.getItem("tenant_has_linked_unit") === "true"
  );

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    setLoadingUser(true);
    setUsername(localStorage.getItem("username") || "Tenant");
    if (token.startsWith("local-token-")) {
      localStorage.setItem("tenant_has_linked_unit", "false");
      setHasLinkedUnit(false);
      setLoadingUser(false);
      return () => {
        active = false;
      };
    }

    Promise.allSettled([api.get("/profile"), api.get("/my-rentals")])
      .then(([profileResult, rentalsResult]) => {
        if (!active) return;

        const profileData =
          profileResult.status === "fulfilled" ? profileResult.value?.data || {} : {};
        const rentalsData =
          rentalsResult.status === "fulfilled" && Array.isArray(rentalsResult.value?.data)
            ? rentalsResult.value.data
            : [];

        const tenantName = profileData?.name || localStorage.getItem("username") || "Tenant";
        localStorage.setItem("username", tenantName);
        setUsername(tenantName);

        const linked = rentalsData.length > 0 || hasLinkedUnitFromProfile(profileData) ||
          (rentalsResult.status === "rejected" && localStorage.getItem("tenant_has_linked_unit") === "true");
        localStorage.setItem("tenant_has_linked_unit", linked ? "true" : "false");
        setHasLinkedUnit(linked);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else if (active) {
          localStorage.setItem("tenant_has_linked_unit", "false");
          setHasLinkedUnit(false);
        }
      })
      .finally(() => {
        if (active) setLoadingUser(false);
      });

    return () => {
      active = false;
    };
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (loadingUser || hasLinkedUnit) {
      return;
    }
    if (!hasPathAccessForApplicant(location.pathname)) {
      navigate("/tenant/apartments", { replace: true });
    }
  }, [hasLinkedUnit, loadingUser, location.pathname, navigate]);

  const handleLogout = () => {
    localStorage.clear();
    if (onLogout) onLogout();
    navigate("/");
  };

  const menuSections = hasLinkedUnit
    ? [
        {
          label: "Overview",
          items: [
            { title: "Dashboard", path: "/tenant/home" },
            { title: "My Profile", path: "/tenant/profile" },
            { title: "My Rentals", path: "/my-rentals" },
          ],
        },
        {
          label: "Payments",
          items: [
            { title: "Submit Payment", path: "/payments" },
            { title: "Payment History", path: "/payment-history" },
            { title: "Monthly Statement", path: "/tenant/monthly-statement" },
          ],
        },
        {
          label: "Support",
          items: [
            { title: "Messages", path: "/tenant/messages" },
            { title: "Maintenance Request", path: "/submit-report" },
            { title: "My Reports", path: "/my-reports" },
            { title: "Announcements", path: "/announcements" },
            { title: "Action Logs", path: "/tenant/notifications" },
          ],
        },
      ]
    : [
        {
          label: "Applicant",
          items: [
            { title: "Dashboard", path: "/tenant/home" },
            { title: "Available Apartments", path: "/tenant/apartments" },
          ],
        },
      ];

  if (loadingUser) {
    return (
      <div className="workspace-theme ws-loading" role="status">
        Loading...
      </div>
    );
  }

  if (hasLinkedUnit && (location.pathname.startsWith("/tenant/apartments") || location.pathname.startsWith("/tenant/apply/"))) {
    return <Navigate to="/tenant/home" replace />;
  }

  return <WorkspaceShell sections={menuSections} username={username} role={hasLinkedUnit ? "Tenant" : "Applicant"} onLogout={handleLogout}>{children || <Outlet context={{ hasLinkedUnit }} />}</WorkspaceShell>;
}
